<script setup lang="ts">
import { useResultStore } from '~/store/result'

defineOptions({
	name: 'BetModeToggle',
})

const props = defineProps({
	disabled: {
		type: Boolean,
		default: false,
	},
})

const { t: $t } = useI18n()
const resultStore = useResultStore()

type Mode = 'standard' | 'multiple'
const mode = ref<Mode>('standard')

const classBtn = 'transition-all duration-200 whitespace-nowrap py-0.5 px-3 text-sm rounded-full border'
const classColorTextActive =
	'bg-[linear-gradient(90deg,var(--secondary-600,#2E2948)_0.11%,#4C3D9C_99.71%)] bg-clip-text text-transparent'
const classInactiveBtn = 'bg-white/50 border-white/10 backdrop-blur font-normal'
const emit = defineEmits<{
	'update:modelValue': [Mode]
	change: [Mode]
}>()

const isActive = (m: Mode) => mode.value === m
const setMode = (m: Mode) => {
	if (props.disabled || m === mode.value) return
	emit('update:modelValue', m)
	emit('change', m)
	mode.value = m
	const status = m === 'multiple'
	resultStore.SET_STATE_RESULT({ type: 'isMutipleBets', value: status })
}

const onKeydown = (e: KeyboardEvent) => {
	if (props.disabled) return
	if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
		e.preventDefault()
		const next: Mode = mode.value === 'standard' ? 'multiple' : 'standard'
		setMode(next)
	}
}

const setInitialValueMode = () => {
	mode.value = resultStore.isMutipleBets ? 'multiple' : 'standard'
}

onMounted(() => {
	setInitialValueMode()
})
</script>

<template>
	<div
		class="flex flex-wrap sm:flex-nowrap items-center gap-2.5"
		role="tablist"
		aria-label="Bet mode"
		@keydown="onKeydown"
	>
		<!-- Standard -->
		<button
			type="button"
			role="tab"
			:aria-selected="isActive('standard')"
			:tabindex="isActive('standard') ? 0 : -1"
			:disabled="disabled"
			:class="[
				classBtn,
				disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
				isActive('standard') ? 'font-medium' : classInactiveBtn,
			]"
			@click="setMode('standard')"
		>
			<span :class="isActive('standard') ? 'text-white' : classColorTextActive"> {{ $t('Standard Check') }} </span>
		</button>

		<!-- Multiple -->
		<button
			type="button"
			role="tab"
			:aria-selected="isActive('multiple')"
			:tabindex="isActive('multiple') ? 0 : -1"
			:disabled="disabled"
			:class="[
				classBtn,
				disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
				isActive('multiple') ? 'font-medium' : classInactiveBtn,
			]"
			@click="setMode('multiple')"
		>
			<span :class="isActive('multiple') ? 'text-white' : classColorTextActive"> {{ $t('Multiple Checks') }} </span>
		</button>
	</div>
</template>
